import pDebounce from 'p-debounce';
import { lock, unlock } from 'tua-body-scroll-lock';

import { contextualError, handleContextualError } from '@/shared/utils/error';

const selectors = {
  COLLECTION: '#collection',
  FILTER_PANEL: '#custom-filter-panel',
  FILTER_PANEL_SCROLLABLE_CONTENT: '#custom-filter-panel-scrollable-content',
  FACET_ACTIVE_TAG: '.js-custom-facet-active-tag',
  FACET_CHECKBOX: '.js-custom-facet-checkbox',
  FACET_CLEAR_ALL: '.js-custom-facet-clear-all',
  FILTER_TOGGLE: '.js-custom-facet-filter-toggle',
  FACET_PRICE_INPUT: '.js-custom-facet-price-input',
  FACET_SORT_RADIO: '.js-custom-facet-sort-radio',
  FACET_SORT_SELECT: '.js-custom-facet-sort-select',
  SEARCH_REPLACE_CONTENT: '.js-shopify-search-replace-content'
};

const allowedPriceKeyupKeys = ['Backspace', 'Delete', 'ArrowUp', 'ArrowDown'];

const allowedPriceKeydownKeys = [
  ...allowedPriceKeyupKeys,
  'Tab',
  'Enter',
  'ArrowLeft',
  'ArrowRight'
];

function onFilterCheckboxChange(this: HTMLInputElement) {
  updateCollection(`${window.location.origin}${this.value}`);
}

function onPriceInputKeydown(this: HTMLInputElement, e: KeyboardEvent) {
  const isAllowed =
    allowedPriceKeydownKeys.includes(e.key) || !isNaN(parseInt(e.key));

  // Prevent keyboard input from updating the field
  if (!isAllowed) {
    e.preventDefault();
    return;
  }
}

function onPriceInputKeyup(this: HTMLInputElement, e: KeyboardEvent) {
  e.preventDefault();

  const isAllowed =
    allowedPriceKeyupKeys.includes(e.key) || !isNaN(parseInt(e.key));

  // Prevent keyboard input from sending request
  if (!isAllowed) {
    return;
  }

  const { minParam, maxParam } = this.dataset;

  const min = document.querySelector<HTMLInputElement>(
    `input[name="${minParam}"]`
  );
  const max = document.querySelector<HTMLInputElement>(
    `input[name="${maxParam}"]`
  );

  const url = new URL(window.location.href);

  if (min) {
    url.searchParams.set(min.name, min.value);
  }
  if (max) {
    url.searchParams.set(max.name, max.value);
  }

  updateCollectionDebounced(url.href);
}

function onActiveTagClick(this: HTMLAnchorElement, e: Event) {
  e.preventDefault();
  updateCollection(this.href);
  // Remove element from DOM
  this.remove();
}

function onClearAllClick(this: HTMLAnchorElement, e: Event) {
  e.preventDefault();
  updateCollection(this.href);
}

function onSortSelectChange(this: HTMLSelectElement, e: Event) {
  e.preventDefault();
  const selectedValue = this.options[this.selectedIndex].value;
  const url = new URL(window.location.href);
  url.searchParams.set('sort_by', selectedValue);
  updateCollection(url.href);
}

function onSortRadioChange(this: HTMLInputElement) {
  const url = new URL(window.location.href);
  url.searchParams.set('sort_by', this.value);
  updateCollection(url.href);
}

let filtersVisible = false;

function toggleFilter(visible: boolean) {
  const filterPanel = document.querySelector<HTMLElement>(
    selectors.FILTER_PANEL
  );
  const filterPanelScrollableContent = document.querySelector<HTMLElement>(
    selectors.FILTER_PANEL_SCROLLABLE_CONTENT
  );

  if (visible) {
    // Lock body scroll
    lock(filterPanelScrollableContent, { overflowType: 'clip' });
  } else {
    // Clear scroll lock
    unlock(filterPanelScrollableContent);
  }

  filterPanel?.classList.toggle('is-active', visible);
}

function onFilterToggleClick(this: HTMLButtonElement) {
  filtersVisible = !filtersVisible;

  toggleFilter(filtersVisible);
}

const setupEventListenersAndInitialState = () => {
  const sortSelect = document.querySelectorAll<HTMLSelectElement>(
    selectors.FACET_SORT_SELECT
  );
  const sortRadios = document.querySelectorAll<HTMLInputElement>(
    selectors.FACET_SORT_RADIO
  );
  const filterCheckboxes = document.querySelectorAll<HTMLInputElement>(
    selectors.FACET_CHECKBOX
  );
  const priceInput = document.querySelectorAll<HTMLInputElement>(
    selectors.FACET_PRICE_INPUT
  );
  const filterActiveTags = document.querySelectorAll<HTMLAnchorElement>(
    selectors.FACET_ACTIVE_TAG
  );
  const filterClearAll = document.querySelectorAll<HTMLAnchorElement>(
    selectors.FACET_CLEAR_ALL
  );
  const filterToggleButton = document.querySelectorAll<HTMLButtonElement>(
    selectors.FILTER_TOGGLE
  );

  // Checkbox filters
  filterCheckboxes.forEach((checkbox) =>
    checkbox.addEventListener('change', onFilterCheckboxChange)
  );

  // Price filters
  priceInput.forEach((input) => {
    // Keydown event is for preventing e.g. letters in the input
    input.addEventListener('keydown', onPriceInputKeydown);
    // Keyup event is for handling the actual input with debounced request
    input.addEventListener('keyup', onPriceInputKeyup);
  });

  // Active filter "Tags" (click to disable filter)
  filterActiveTags.forEach((link) =>
    link.addEventListener('click', onActiveTagClick)
  );
  filterClearAll.forEach((link) =>
    link.addEventListener('click', onClearAllClick)
  );

  // Sort by filter (desktop)
  sortSelect.forEach((select) =>
    select.addEventListener('change', onSortSelectChange)
  );
  // Sort by filter (mobile)
  sortRadios.forEach((radio) =>
    radio.addEventListener('change', onSortRadioChange)
  );

  // Toggle filter panel on mobile
  filterToggleButton.forEach((button) =>
    button.addEventListener('click', onFilterToggleClick)
  );
};

const toggleAnchorDisabled = (el: HTMLAnchorElement, disabled: boolean) => {
  el.classList.toggle('pointer-events-none', disabled);
};

const toggleInputDisabled = (
  el: HTMLInputElement | HTMLSelectElement,
  disabled: boolean
) => {
  el.disabled = disabled;
};

const toggleLoadingState = (isLoading: boolean) => {
  const sortSelect = document.querySelectorAll<HTMLSelectElement>(
    selectors.FACET_SORT_SELECT
  );
  const filterCheckboxes = document.querySelectorAll<HTMLInputElement>(
    selectors.FACET_CHECKBOX
  );
  const filterActiveTags = document.querySelectorAll<HTMLAnchorElement>(
    selectors.FACET_ACTIVE_TAG
  );
  const filterPanelScrollableContent = document.querySelector<HTMLElement>(
    selectors.FILTER_PANEL_SCROLLABLE_CONTENT
  );
  const collection = document.querySelector<HTMLElement>(selectors.COLLECTION);

  filterPanelScrollableContent?.classList.toggle('is-loading', isLoading);
  collection?.classList.toggle('is-loading', isLoading);
  filterActiveTags.forEach((el) => toggleAnchorDisabled(el, isLoading));
  // Only set disabled on loading, not clearing disabled when loading is finished, as the disabled state should then be handled by SSR (liquid response)
  if (isLoading) {
    sortSelect.forEach((el) => toggleInputDisabled(el, isLoading));
    filterCheckboxes.forEach((el) => toggleInputDisabled(el, isLoading));
  }
};

const updateCollection = async (href: string) => {
  const url = new URL(href);

  // update the URL without refreshing the page
  window.history.replaceState({}, '', url);

  // add the sections parameter to the URL, to only fetch the collection and collection header sections
  url.searchParams.set(
    'sections',
    'collection,collection-header,search-header'
  );

  toggleLoadingState(true);
  // Fetch data based on the href of the clicked element

  let data;
  try {
    const response = await fetch(url);
    data = await response.json();

    //
    // Update DOM
    // We avoid updating the entire filter section in order to keep the state of filter tabs and scroll state on mobile filter
    replaceSearchContentFromResponse(data.collection);

    if (data['collection-header']) {
      replaceSearchContentFromResponse(data['collection-header']);
    }

    if (data['search-header']) {
      replaceSearchContentFromResponse(data['search-header']);
    }

    setupEventListenersAndInitialState();
    toggleLoadingState(false);
  } catch (error) {
    handleContextualError(
      contextualError('UpdateCollectionError', error, {
        data
      })
    );
  }
};

const updateCollectionDebounced = pDebounce(updateCollection, 1000);

const getHtmlFromString = (htmlStr: string) => {
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlStr;

  return {
    cleanup: () => tempDiv.remove(),
    html: tempDiv
  };
};

const replaceSearchContentFromResponse = (responseHtmlStr: string) => {
  const { html, cleanup } = getHtmlFromString(responseHtmlStr);
  const searchContainers = html.querySelectorAll<HTMLElement>(
    selectors.SEARCH_REPLACE_CONTENT
  );
  const priceInput = html.querySelectorAll<HTMLInputElement>(
    selectors.FACET_PRICE_INPUT
  );

  // Dynamically update DOM from response
  searchContainers.forEach((el) => {
    if (!el.id)
      throw new Error(
        `Could not find id for Shopify search element with class ${el.className}: Could not update DOM`
      );
    // Replace the content of divWrapper with div's content
    const targetElement = document.getElementById(el.id);

    // Skip if the element from response is not in the current DOM
    // (this can happen because Search and Collection have slightly differing templates)
    if (!targetElement) return;

    // Replace the content of targetElement with div's content
    targetElement.innerHTML = el.innerHTML;
  });

  // Update all price input values from response
  // This is needed when clearing all filters
  priceInput.forEach((el) => {
    if (!el.id)
      throw new Error(
        `Could not find id for Shopify price input element with class ${el.className}: Could not update DOM`
      );
    // Replace the content of divWrapper with div's content
    const targetElement = document.getElementById(
      el.id
    ) as HTMLInputElement | null;

    if (!targetElement)
      throw new Error(`No element with id ${el.id} found in the current DOM`);

    // Replace the input value of targetElement with value from response
    targetElement.value = el.value;
  });

  cleanup();
};

setupEventListenersAndInitialState();
